import React, { useEffect, useState } from 'react'
import { INTEGRATIONS_SCHEMA } from '../../../config/integrations-schema'
import { saveIntegrationConfig, loadIntegrationConfig } from '../../../lib/integrations-store'
import { runIntegrationTest } from '../../../lib/integration-tests'
import Icon from '../../AppIcon'

// Mask a secret value showing only last 4 chars
const maskSecret = (value) => {
  if (!value || value.length < 8) return '••••••••'
  return '••••••••' + value.slice(-4)
}

export default function IntegrationPanel({ name, meta = {} }) {
  const spec = INTEGRATIONS_SCHEMA[name] || { title: name, fields: [] }

  const [isEditing, setIsEditing] = useState(false)
  const [config, setConfig] = useState({})
  const [showSecrets, setShowSecrets] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [saveError, setSaveError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Load config from Firestore on mount
  useEffect(() => {
    const loadConfig = async () => {
      setIsLoading(true)
      try {
        // Get defaults from schema
        const defaults = {}
        for (const f of (spec.fields || [])) {
          if (f.default) defaults[f.name] = f.default
        }

        // Load from Firestore
        const result = await loadIntegrationConfig(name)

        if (result.success) {
          setConfig({ ...defaults, ...result.config })
        } else {
          setConfig(defaults)
        }
      } catch (err) {
        console.error('Failed to load config:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadConfig()
  }, [name, spec.fields])

  const hasConfig = Object.values(config).some(v => v && String(v).trim().length > 0)
  const isConnected = meta?.status === 'OK' || meta?.hasSecret || hasConfig

  // Toggle secret visibility
  const toggleShowSecret = (fieldName) => {
    setShowSecrets(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))
  }

  // Handle save to Firestore
  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)
    setSaveSuccess(false)

    try {
      const result = await saveIntegrationConfig(name, config)

      if (result.success) {
        setSaveSuccess(true)
        setIsEditing(false)

        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000)
      } else {
        setSaveError(result.error || 'Failed to save')
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle test - uses direct API test
  const handleTest = async () => {
    setIsTesting(true)
    setTestResult(null)
    try {
      const result = await runIntegrationTest(name, config)
      setTestResult(result)
    } catch (err) {
      setTestResult({ ok: false, error: err.message || 'Test failed' })
    } finally {
      setIsTesting(false)
    }
  }

  // Render field value (masked or visible)
  const renderFieldValue = (field, value) => {
    if (!value) return <span className="text-muted-foreground italic">Not configured</span>

    if (field.type === 'secret') {
      const isVisible = showSecrets[field.name]
      return (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm">
            {isVisible ? value : maskSecret(value)}
          </span>
          <button
            onClick={() => toggleShowSecret(field.name)}
            className="p-1 hover:bg-muted rounded transition-colors"
            title={isVisible ? 'Hide' : 'Show'}
          >
            <Icon name={isVisible ? 'EyeOff' : 'Eye'} size={14} className="text-muted-foreground" />
          </button>
        </div>
      )
    }

    if (field.type === 'textarea') {
      return <span className="text-sm text-muted-foreground truncate max-w-[200px] block">{String(value).slice(0, 50)}...</span>
    }

    return <span className="text-sm">{value}</span>
  }

  // Render input field
  const renderInput = (field) => {
    const value = config[field.name] || ''

    if (field.type === 'select') {
      return (
        <select
          className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
          value={value}
          onChange={(e) => setConfig({ ...config, [field.name]: e.target.value })}
        >
          {(field.options || []).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none min-h-[80px] font-mono bg-white"
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => setConfig({ ...config, [field.name]: e.target.value })}
        />
      )
    }

    return (
      <input
        type={field.type === 'secret' ? 'password' : 'text'}
        className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => setConfig({ ...config, [field.name]: e.target.value })}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={spec.icon || 'Plug'} size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{spec.title}</h3>
              <p className="text-xs text-muted-foreground">{spec.description}</p>
            </div>
          </div>
        </div>
        <div className="p-8 flex items-center justify-center">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={spec.icon || 'Plug'} size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{spec.title}</h3>
              <p className="text-xs text-muted-foreground">{spec.description}</p>
            </div>
          </div>

          {/* Status Badge */}
          <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
            isConnected
              ? 'bg-primary/10 text-foreground'
              : 'bg-muted text-muted-foreground'
          }`}>
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-primary' : 'bg-muted-foreground'}`} />
            {isConnected ? 'Connected' : 'Not Connected'}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isEditing ? (
          /* Edit Mode */
          <div className="space-y-4">
            {(spec.fields || []).map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </label>
                {renderInput(field)}
                {field.help && (
                  <p className="text-xs text-muted-foreground mt-1">{field.help}</p>
                )}
              </div>
            ))}

            {saveError && (
              <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                {saveError}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <Icon name="Loader2" size={16} className="animate-spin" />
                ) : (
                  <Icon name="Check" size={16} />
                )}
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* View Mode */
          <div className="space-y-3">
            {(spec.fields || []).map((field) => {
              const value = config[field.name]
              return (
                <div key={field.name} className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
                  <span className="text-sm text-muted-foreground">{field.label}</span>
                  {renderFieldValue(field, value)}
                </div>
              )
            })}

            {/* Success message */}
            {saveSuccess && (
              <div className="p-2 bg-success/10 border border-success/20 rounded-lg text-sm text-success flex items-center gap-2">
                <Icon name="CheckCircle" size={16} />
                Saved to cloud!
              </div>
            )}

            {/* Last Test Result */}
            {testResult?.testedAt && (
              <div className="pt-2 text-xs text-muted-foreground flex items-center gap-2">
                <Icon name="Clock" size={12} />
                Last tested: {new Date(testResult.testedAt).toLocaleString()}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => {
                  setSaveSuccess(false)
                  setIsEditing(true)
                }}
                className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors flex items-center justify-center gap-2"
              >
                <Icon name="Pencil" size={16} />
                Edit
              </button>
              <button
                onClick={handleTest}
                disabled={isTesting}
                className="flex-1 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isTesting ? (
                  <Icon name="Loader2" size={16} className="animate-spin" />
                ) : (
                  <Icon name="Zap" size={16} />
                )}
                {isTesting ? 'Testing...' : 'Test'}
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${
                testResult.ok
                  ? 'bg-success/10 border border-success/20 text-success'
                  : 'bg-destructive/10 border border-destructive/20 text-destructive'
              }`}>
                <div className="flex items-center gap-2">
                  <Icon
                    name={testResult.ok ? 'CheckCircle' : 'XCircle'}
                    size={16}
                  />
                  <span className="font-medium">
                    {testResult.ok
                      ? (testResult.message || 'Connection successful')
                      : 'Connection failed'}
                  </span>
                  {testResult.latencyMs && (
                    <span className="text-xs opacity-70">({testResult.latencyMs}ms)</span>
                  )}
                </div>
                {testResult.error && (
                  <p className="mt-1 text-xs opacity-80">{testResult.error}</p>
                )}
                {testResult.hint && (
                  <p className="mt-1 text-xs opacity-70 italic">{testResult.hint}</p>
                )}
                {testResult.response && (
                  <p className="mt-1 text-xs font-mono bg-muted px-2 py-1 rounded">
                    Response: {testResult.response}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
